import _get from 'lodash/get';

import {
    configureEsntialListing,
    configurePsxListing,
    obfuscateCarfaxHref,
    replaceImageServerInUrl,
    setPhoneVisible,
    translateListingPriority,
} from '@/utilities/dispatchUtils';
import getAvailableOwnerTimes from '@/utilities/getAvailableOwnerTimes';
import getFilteredVHRTiles from '@/utilities/getFilteredVHRTiles';
import processPaymentServices from '@/utilities/processPaymentServices';

import expiredListingDuck from '@/ducks/expiredListingDuck.js';
import globalConfigsDuck from '@/ducks/globalConfigsDuck.js';
import inventoryDuck from '@/ducks/inventoryDuck.js';
import ownersDuck from '@/ducks/ownersDuck.js';
import vdpActiveEmailListingDuck from '@/ducks/vdp/vdpActiveEmailListingDuck.js';
import vdpAdsDuck from '@/ducks/vdp/vdpAdsDuck.js';
import vdpContentDuck from '@/ducks/vdp/vdpContentDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';
import vdpVideoDuck from '@/ducks/vdp/vdpVideoDuck.js';

import buildSpecifications from './buildSpecifications';

export default function dispatchVehicleDetailsPageData() {
    return async (ctx) => {

        const state = ctx.store.getState();
        const isAtc = globalConfigsDuck.selectors.isAtc(state);
        const isKbb = globalConfigsDuck.selectors.isKbb(state);
        const brand = state?.brand;
        const { creditApp = {} } = vdpResultsDuck.selectors.getVdpActiveInventory(state) || {};

        // Create batched action queue
        ctx.batchDispatch.createActions((deferredCtx) => {

            const {
                pageData = {},
            } = deferredCtx.data;

            // account for lsc or cs response
            const listing = deferredCtx?.data?.listings?.[0] || deferredCtx?.data;
            const {
                images,
                fbaCarfaxReport,
            } = listing;
            const owner = deferredCtx?.data?.owners?.[0] || deferredCtx?.data?.owner || {};
            listing.ownerName = owner?.name;

            const obfuscatedProductTiles = obfuscateCarfaxHref(listing);

            listing.productTiles = obfuscatedProductTiles;

            // Feature Flags
            const {
                brand: [, {
                    inventory_image_url: inventoryImageUrl, alwaysShowOwnerPhone,
                    paymentDealerId,
                    paymentPartnerId,
                }],
                dealer_website_link: [transformDealerWebsiteLink, dealerWebsiteConfig],
                override_carfax_tile: [isOverrideCarfaxTileEnabled],
                safeguard: [isSafeGuardEnabled],
                standard_listing_phone_display: [displayPhoneNumber],
                vdp_safeguard: [isSafeGuardEnabledOptimizely],
                nds_calc: [, { forceDefaultRates }],
                nds_lease: [isNdsLeaseEnabled, leaseEnabledDRTypes = {}],
                nds_price_breakdown: [isPriceBreakdownEnabled, priceBkdwnEnabledDRTypes = {}],
                non_dt_payment_services: [enabledNonDTPaymentServices],
                use_fba_payment_calc: [enableFBAPaymentCalc],
            } = ctx.useFeatures([
                'brand',
                'dealer_website_link',
                'override_carfax_tile',
                'safeguard',
                'standard_listing_phone_display',
                'vdp_safeguard',
                'nds_calc',
                'nds_lease',
                'nds_price_breakdown',
                'non_dt_payment_services',
                'use_fba_payment_calc',
            ]);

            listing.vhrTiles = getFilteredVHRTiles(obfuscatedProductTiles, fbaCarfaxReport?.reportUrl, isOverrideCarfaxTileEnabled);

            configurePsxListing({ listing, owner });

            // Replace image server in source image URl
            const imageSrc = _get(images, 'sources', null);
            if (inventoryImageUrl && imageSrc) {
                Object.values(imageSrc).forEach((image) => {
                    replaceImageServerInUrl(image, 'src', inventoryImageUrl);
                });
            }

            // Check for safeguard feature and override if it's an unsupported value
            if (!isSafeGuardEnabledOptimizely || !isSafeGuardEnabled || !['30', '60', '90'].includes(listing?.safeGuardProgram)) {
                delete listing.safeGuardProgram;
            }

            // Bypass phone number rules for standard listings in some experiences
            setPhoneVisible(owner, displayPhoneNumber, alwaysShowOwnerPhone);

            const isEcommHyundai = deferredCtx.query.experience === 'hyundai-buy-online';

            configureEsntialListing({
                shouldRemoveUtmParams: isAtc || isKbb,
                dealerWebsiteConfig,
                listing,
                owner,
                transformDealerWebsiteLink,
                isEcommHyundai,
            });

            // Add scheduling properties to owner
            if (owner.hours) {
                try {
                    const ownerTimes = getAvailableOwnerTimes(owner.hours);
                    owner.availableTimes = ownerTimes?.availableTimes;
                    owner.disabledDaysOfWeek = ownerTimes?.disabledDaysOfWeek;
                    owner.nextOpenDate = ownerTimes?.nextOpenDate;

                    // If bad data, disable lead scheduling
                    if (owner.disabledDaysOfWeek.length === 7
                        || Object.keys(owner.availableTimes)
                            .filter((key) => owner.availableTimes[key].length).length === 0) {
                        listing.leadScheduling = false;
                    }
                } catch (e) {
                    listing.leadScheduling = false;
                }
            } else {
                listing.leadScheduling = false;
            }

            const listingId = listing?.id || deferredCtx.query.listingId;

            if (!listing.hasDealerDiff && owner.dealerDiff) {
                listing.hasDealerDiff = true;
            }

            if (!listing.hasPlaylist && listing.videos) {
                listing.hasPlaylist = listing.videos.some((video) => video.src.includes('xspf'));
            }

            if (Number.isInteger(parseInt(listing?.priority, 10))) {
                translateListingPriority(listing);
            }

            listing.similarInventoryConquest = listing.priority === 'FREE' || listing.priority === '5' || listing.owner?.privateSeller;

            if (deferredCtx.query?.clickType && !listing.clickType) {
                listing.clickType = deferredCtx.query.clickType;
            }

            listing.specifications = buildSpecifications(listing);

            // Process paymentServices
            if (listing.paymentServices) {
                processPaymentServices(listing, {
                    paymentDealerId,
                    paymentPartnerId,
                    forceDefaultRates,
                    isNdsLeaseEnabled,
                    leaseEnabledDRTypes,
                    priceBkdwnEnabledDRTypes,
                    isPriceBreakdownEnabled,
                    enabledNonDTPaymentServices,
                    enableFBAPaymentCalc,
                }, isEcommHyundai);
            }

            const activeInventory = {
                ...listing,
                owner,
            };

            if (Object.keys(creditApp).length && owner?.privateSeller) { // adding psx credit app srp to vdp navigation
                activeInventory.creditApp = creditApp;
            } else {
                delete activeInventory.creditApp;
            }

            return [
                vdpContentDuck.creators.assign({
                    metaDescription: pageData.metaDescription,
                    metaKeywords: pageData.metaKeywords || '',
                    metaRobots: pageData.metaRobots,
                    windowTitle: pageData.windowTitle || pageData.title,
                    openGraphDescription: pageData.openGraphDescription,
                    openGraphImage: pageData.openGraphImage,
                    openGraphSiteName: pageData.openGraphSiteName,
                    openGraphTitle: pageData.windowTitle || pageData.title,
                    openGraphType: pageData.openGraphType,
                    openGraphUrl: pageData.openGraphUrl,
                }),

                // set the vdp ad paramters
                vdpAdsDuck.creators.setKeys(_get(pageData, 'ad', {})),

                // Add owner to listings collection
                ownersDuck.creators.addOwners({ [owner?.id]: owner }),

                // Update the inventory collection
                inventoryDuck.creators.addVdpInventory(activeInventory, brand),

                // Update the video collection
                vdpVideoDuck.creators.setActiveResults(_get(listing, 'videos', [])),

                // emailOwner
                vdpActiveEmailListingDuck.creators.setActiveResults([listingId]),

                // Map the order to display results
                vdpResultsDuck.creators.setActiveResults([listingId]),

                // Update listing fetching status if VDP endpoint fails
                vdpResultsDuck.creators.setFetchError(!listing.id),

                expiredListingDuck.creators.setKeys(listing?.expiredListing),
            ];

        });
        // dispatch the batched action queue
        await ctx.batchDispatch.dispatchActions(ctx);
    };
}
